import { useEffect, useId, useRef, useState } from 'react'
import { useDealNameSuggestions } from '../../api/queries/deals'
import { useBillingStatus } from '../../api/queries/billing'
import { OutOfAnalysesDialog } from '../billing/OutOfAnalysesDialog'
import { asDuplicateDeal, asUploadRefusal, useUploadTranscript } from '../../api/queries/upload'
import type { DuplicateDeal, UploadRefusal } from '../../api/queries/upload'
import { userFacingMessage } from '../../api/errors'
import { Button } from '../../ui/Button'
import { TextField } from '../../ui/TextField'
import { Select } from '../../ui/Select'
import { COUNTRIES } from '../../lib/countries'
import { AnalysisProgress } from './AnalysisProgress'
import styles from './Upload.module.css'

type Phase =
  | { name: 'form' }
  | { name: 'duplicate'; detail: DuplicateDeal }
  | { name: 'refusal'; detail: UploadRefusal }
  | { name: 'running'; jobId: string }

/**
 * Add a call transcript.
 *
 * Entitlement is checked BEFORE the user picks a file, so someone out of credits
 * is told plainly instead of meeting a 402 as a red toast (audit section 7.1).
 * There is no purchase button here, because purchasing does not exist yet — a
 * control ships only when its backend behaviour does (docs/16 section 6.5).
 */
export function UploadDialog({ onClose, dealId }: { onClose: () => void; dealId?: string }) {
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const firstRef = useRef<HTMLButtonElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [company, setCompany] = useState('')
  const [country, setCountry] = useState('')
  const [phase, setPhase] = useState<Phase>({ name: 'form' })

  const dealSuggestions = useDealNameSuggestions()
  const billing = useBillingStatus()
  const upload = useUploadTranscript()

  const attaching = Boolean(dealId)
  const blocked = billing.data ? !billing.data.usage.can_run_analysis && !billing.data.unmetered : false

  useEffect(() => {
    firstRef.current?.focus()
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape' && phase.name !== 'running') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, phase.name])

  async function submit(
    options: { forceNewDeal?: boolean; attachTo?: string; forceDuplicateTranscript?: boolean; forceInactiveDeal?: boolean } = {},
  ) {
    if (!file) return
    try {
      const accepted = await upload.mutateAsync({
        file,
        dealId: options.attachTo ?? dealId,
        companyName: attaching || options.attachTo ? undefined : company.trim(),
        country: attaching || options.attachTo ? undefined : country || undefined,
        forceNewDeal: options.forceNewDeal,
        forceDuplicateTranscript: options.forceDuplicateTranscript,
        forceInactiveDeal: options.forceInactiveDeal,
      })
      setPhase({ name: 'running', jobId: accepted.job_id })
    } catch (error) {
      const duplicate = asDuplicateDeal(error)
      if (duplicate) {
        setPhase({ name: 'duplicate', detail: duplicate })
        return
      }
      const refusal = asUploadRefusal(error)
      if (refusal) {
        setPhase({ name: 'refusal', detail: refusal })
        return
      }
      // Anything else stays on the form with its message shown; never swallowed.
    }
  }

  const canSubmit = Boolean(file) && (attaching || company.trim().length > 0) && !upload.isPending && !blocked

  return (
    <>
      {/* The wall stands in front of the form, not inside it: there is nothing to
          fill in until one of its routes has been taken, and Cancel closes both. */}
      {blocked && billing.data && phase.name === 'form' ? <OutOfAnalysesDialog status={billing.data} onClose={onClose} /> : null}
      <div className={styles.overlay} aria-hidden="true" onClick={phase.name === 'running' ? undefined : onClose} />
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby={titleId} ref={dialogRef}>
        <h2 id={titleId} className={styles.title}>
          {attaching ? 'Add a call to this deal' : 'Analyse a new transcript'}
        </h2>

        {phase.name === 'running' ? (
          <AnalysisProgress jobId={phase.jobId} onClose={onClose} />
        ) : phase.name === 'refusal' ? (
          <div className={styles.body}>
            <p className={styles.explain}>{phase.detail.message}</p>
            {phase.detail.code === 'duplicate_transcript' && phase.detail.companyName ? (
              <p className={styles.hint}>
                Analysed as call {phase.detail.callNumber} on {phase.detail.companyName}.
              </p>
            ) : null}
            <div className={styles.actions}>
              <Button
                onClick={() =>
                  void submit(
                    phase.detail.code === 'duplicate_transcript'
                      ? { forceDuplicateTranscript: true, attachTo: dealId }
                      : { forceInactiveDeal: true, attachTo: dealId },
                  )
                }
              >
                Analyse it anyway
              </Button>
              <Button onClick={onClose}>Cancel</Button>
            </div>
          </div>
        ) : phase.name === 'duplicate' ? (
          <div className={styles.body}>
            <p className={styles.explain}>{phase.detail.message}</p>
            <ul className={styles.candidates}>
              {phase.detail.candidates.map((candidate) => (
                <li key={candidate.deal_id}>
                  <Button onClick={() => void submit({ attachTo: candidate.deal_id })}>
                    Add to {candidate.company_name}
                  </Button>
                </li>
              ))}
            </ul>
            <div className={styles.actions}>
              <Button onClick={() => void submit({ forceNewDeal: true })}>This is a new deal</Button>
              <Button onClick={onClose}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div className={styles.body}>

            <label className={styles.file}>
              <span className={styles.fileLabel}>Transcript file</span>
              <input
                ref={undefined}
                type="file"
                accept=".pdf,.docx,.txt"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
              <span className={styles.hint}>PDF, DOCX or TXT.</span>
            </label>

            {attaching ? (
              <p className={styles.explain}>
                This call will be added to the deal you have open. The company is already known.
              </p>
            ) : (
              <>
                <TextField
                  label="Company"
                  list="dp-companies"
                  placeholder="Pick an existing company, or type a new one"
                  value={company}
                  onChange={(event) => setCompany(event.target.value)}
                />
                <datalist id="dp-companies">
                  {dealSuggestions.map((deal) => (
                    <option key={deal.id} value={deal.companyName} />
                  ))}
                </datalist>
                <Select
                  label="Country (optional)"
                  value={country}
                  onChange={(event) => setCountry(event.target.value)}
                  options={[{ value: '', label: 'Not specified' }, ...COUNTRIES.map((c) => ({ value: c, label: c }))]}
                />
              </>
            )}

            {upload.isError ? (
              <p className={styles.error} role="alert">
                {userFacingMessage(upload.error)}
              </p>
            ) : null}

            <div className={styles.actions}>
              <Button ref={firstRef} variant="primary" disabled={!canSubmit} onClick={() => void submit()}>
                {upload.isPending ? 'Uploading…' : 'Analyse'}
              </Button>
              <Button onClick={onClose}>Cancel</Button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
